import React, { useState, useEffect } from 'react';
import { apiFetch, getRole } from './api';
import { UserRound } from 'lucide-react';

export default function HomeScreen({ 
    onLogout, 
    onSystemAdmin, 
    users, 
    selectedUserId, 
    setSelectedUserId,
    newUserName,
    setNewUserName,
    createUser,
    testMode,
    setTestMode,
    testModality,
    setTestModality,
    testedEye,
    setTestedEye,
    onStart,
    onViewHistory,
    onRecalibrate,
    onAdmin,
    calibrationData,
    onForceTutorial
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [currentUsername, setCurrentUsername] = useState('');
  
  const [showEyeSelectionModal, setShowEyeSelectionModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [lastTests, setLastTests] = useState({ right: null, left: null, both: null });

  useEffect(() => {
    apiFetch('/api/auth/me').then(res => res.ok ? res.json() : null).then(data => {
      if(data) setCurrentUsername(data.username);
    }).catch(e => console.error(e));
  }, []);


  const handleChangePassword = async () => {
    if (!newPassword.trim()) return;
    try {
      const meRes = await apiFetch('/api/auth/me');
      if (meRes.ok) {
        const me = await meRes.json();
        const res = await apiFetch(`/api/auth/users/${me.id}/password`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_password: newPassword })
        });
        if (res.ok) {
          alert("Password changed successfully! Please log in again.");
          onLogout();
        } else {
          alert("Failed to change password.");
        }
      }
    } catch (e) {
      alert("Error changing password.");
    }
  };

  const formatLastTestTime = (isoString) => {
    if (!isoString) return "Never tested";
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = diffMs / 1000;
    const diffMin = diffSec / 60;
    const diffHours = diffMin / 60;
    const diffDays = diffHours / 24;

    const isSameDay = date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    
    if (diffSec < 60) return "less than a minute ago";
    if (diffMin < 60) return `${Math.floor(diffMin)} minutes ago`;
    if (isSameDay) return `${Math.floor(diffHours)} hours ago`;
    
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.getDate() === yesterday.getDate() && date.getMonth() === yesterday.getMonth() && date.getFullYear() === yesterday.getFullYear();
    
    if (isYesterday) return "yesterday";
    if (diffDays < 30) return `${Math.floor(diffDays)} days ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getTimeColor = (isoString) => {
    if (!isoString) return "#fa5252"; // Red (Never)
    const date = new Date(isoString);
    const now = new Date();
    const isSameDay = date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    if (isSameDay) return "#40c057"; // Green
    const diffDays = (now - date) / (1000 * 60 * 60 * 24);
    if (diffDays < 7) return "#fab005"; // Yellow
    return "#fa5252"; // Red
  };

  const handleStartInitiated = async () => {
    if (!selectedUserId) {
        alert("Please select a patient first.");
        return;
    }
    try {
        const res = await apiFetch(`/api/patients/${selectedUserId}/last_tests`);
        if (res.ok) {
            const data = await res.json();
            setLastTests(data);
        }
    } catch (e) {
        console.error(e);
    }
    setShowEyeSelectionModal(true);
  };

  return (
    <div className="home-screen">
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 10 }}>
        <div 
          onClick={() => setMenuOpen(!menuOpen)} 
          style={{ 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            width: '40px', height: '40px', borderRadius: '50%', 
            backgroundColor: '#333', cursor: 'pointer',
            border: '1px solid #555'
          }}
        >
          <UserRound color="#ccc" size={24} />
        </div>
        {menuOpen && (
          <div style={{ 
            position: 'absolute', top: '50px', right: '0', 
            backgroundColor: '#222', border: '1px solid #444', 
            borderRadius: '4px', padding: '10px', display: 'flex', 
            flexDirection: 'column', gap: '5px', minWidth: '150px',
            zIndex: 10
          }}>
            <div style={{ padding: '8px', color: '#888', fontSize: '12px', borderBottom: '1px solid #444', marginBottom: '5px' }}>
              Logged in as: <strong style={{color: '#fff'}}>{currentUsername}</strong>
            </div>
            <button 
              onClick={() => { setMenuOpen(false); setShowPasswordModal(true); }}
              style={{ background: 'none', border: 'none', color: 'white', textAlign: 'left', padding: '8px', cursor: 'pointer', width: '100%' }}
            >
              Change Password
            </button>
            <button 
              onClick={onLogout}
              style={{ background: 'none', border: 'none', color: '#fa5252', textAlign: 'left', padding: '8px', cursor: 'pointer', width: '100%' }}
            >
              Logout
            </button>
          </div>
        )}
      </div>

      {showPasswordModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }}>
          <div className="card" style={{ padding: '30px', maxWidth: '400px', width: '100%' }}>
            <h3>Change Password</h3>
            <input 
              type="password" 
              placeholder="Enter new password" 
              value={newPassword} 
              onChange={e => setNewPassword(e.target.value)} 
              style={{ width: '100%', padding: '10px', marginTop: '15px', borderRadius: '4px', border: '1px solid #444', background: '#222', color: 'white' }}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowPasswordModal(false)} style={{ background: 'transparent', color: '#888', border: 'none', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleChangePassword} className="btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}

      <h1>maculy</h1>
      <h2>Smart Vision Monitoring</h2>
      
      <div className="card">
          <div className="input-group">
            <label>Select Patient</label>
            <select 
              value={selectedUserId || ''} 
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="" disabled>-- Select Patient --</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>

          <div className="input-group new-user">
            <input 
              type="text" 
              value={newUserName} 
              onChange={(e) => setNewUserName(e.target.value)} 
              placeholder="Or create new patient"
            />
            <button className="btn-secondary" onClick={createUser}>Add</button>
          </div>
      </div>

      <div className="card">
          <div className="input-group">
            <label>Test Mode</label>
            <select 
              value={testMode} 
              onChange={(e) => setTestMode(e.target.value)}
            >
              <option value="monitoring">Daily Monitoring (Fast)</option>
              <option value="baseline">Baseline Init (Long)</option>
            </select>
          </div>

          <div className="input-group">
            <label>Test Modality</label>
            <select 
              value={testModality} 
              onChange={(e) => setTestModality(e.target.value)}
            >
              <option value="full">Full (Contrast + Color)</option>
              <option value="csf">Contrast Only</option>
              <option value="color">Color Only</option>
            </select>
          </div>


      </div>

      <button className="btn-primary" onClick={handleStartInitiated}>Start Test</button>
      
      <div style={{ display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'center' }}>
          <button className="btn-secondary" onClick={onViewHistory} style={{ padding: '10px 20px' }}>History</button>
          <button className="btn-secondary" onClick={onForceTutorial} style={{ padding: '10px 20px' }}>Tutorial</button>
          <button className="btn-secondary" onClick={onRecalibrate} style={{ padding: '10px 20px' }}>
              {calibrationData ? 'Recalibrate' : 'Calibrate'}
          </button>
      </div>
      {calibrationData && (
          <p style={{ marginTop: '10px', opacity: 0.6, fontSize: '0.85rem' }}>
              Screen calibrated at {calibrationData.viewingDistanceCm} cm.
          </p>
      )}

      <div style={{ marginTop: '50px' }}>
        <button onClick={onAdmin} style={{ background: 'none', border: '1px solid #444', color: '#888', padding: '5px 15px', fontSize: '12px', marginRight: '10px', cursor: 'pointer', borderRadius: '4px' }}>
          Data Analysis
        </button>
        {(getRole() === 'ADMIN' || getRole() === 'SUPERADMIN') && (
        <button onClick={onSystemAdmin} style={{ background: 'none', border: '1px solid #444', color: '#888', padding: '5px 15px', fontSize: '12px', cursor: 'pointer', borderRadius: '4px' }}>
          System Users
        </button>
        )}
      </div>

      {showEyeSelectionModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#111', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100, overflowY: 'auto' }}>
          <div style={{ padding: '20px', maxWidth: '500px', width: '100%', textAlign: 'center' }}>
            <h2 style={{ marginBottom: '10px', color: 'white' }}>Select Eye to Test</h2>
            <div style={{color: '#888', marginBottom: '30px', fontSize: '1.2rem'}}>Patient: <strong style={{color: 'white'}}>{users.find(u => String(u.id) === String(selectedUserId))?.name}</strong></div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}>
                
                {['right', 'left', 'both'].map(eyeKey => (
                    <div key={eyeKey} style={{ flex: 1, backgroundColor: '#2a2a2a', padding: '25px', borderRadius: '12px', cursor: 'pointer', border: '2px solid transparent' }} 
                         onClick={() => { setTestedEye(eyeKey); setShowEyeSelectionModal(false); setShowConfirmationModal(true); }}
                         onMouseOver={e => e.currentTarget.style.borderColor = '#339af0'}
                         onMouseOut={e => e.currentTarget.style.borderColor = 'transparent'}
                    >
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '10px', color: 'white', textTransform: 'capitalize' }}>
                            {eyeKey === 'both' ? 'Binocular' : `${eyeKey} Eye`}
                        </div>
                        <div style={{ color: getTimeColor(lastTests[eyeKey]), fontSize: '1.1rem', fontWeight: 'bold' }}>
                            {formatLastTestTime(lastTests[eyeKey])}
                        </div>
                    </div>
                ))}
            </div>
            <button onClick={() => setShowEyeSelectionModal(false)} className="btn-secondary" style={{ padding: '20px', fontSize: '1.2rem', width: '100%', borderRadius: '12px' }}>Cancel</button>
          </div>
        </div>
      )}

      {showConfirmationModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#111', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100, overflowY: 'auto' }}>
          <div style={{ padding: '20px', maxWidth: '500px', width: '100%', textAlign: 'center' }}>
            <h2 style={{ marginBottom: '20px', color: '#fa5252', fontSize: '2rem' }}>Identity Confirmation</h2>
            <p style={{ fontSize: '1.4rem', color: '#ccc', marginBottom: '40px', lineHeight: '1.5' }}>
                You are testing patient <strong style={{ color: 'white' }}>{users.find(u => String(u.id) === String(selectedUserId))?.name}</strong> with the <strong style={{ color: 'white', textTransform: 'capitalize' }}>{testedEye === 'both' ? 'Binocular' : `${testedEye} Eye`}</strong> setup.
                <br/><br/>Please confirm.
            </p>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexDirection: 'column' }}>
                <button onClick={() => { setShowConfirmationModal(false); onStart(); }} className="btn-primary" style={{ padding: '20px', backgroundColor: '#40c057', fontSize: '1.3rem', borderRadius: '12px', border: 'none', color: 'white', fontWeight: 'bold' }}>Confirm & Start</button>
                <button onClick={() => setShowConfirmationModal(false)} className="btn-secondary" style={{ padding: '20px', backgroundColor: '#fa5252', fontSize: '1.3rem', borderRadius: '12px', border: 'none', color: 'white', fontWeight: 'bold' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


